import React from 'react';
import { Flex, Text, TextField, TextArea, Switch, Separator, Button, Tooltip } from "@radix-ui/themes";
import { InstagramLogoIcon, UploadIcon } from "@radix-ui/react-icons";

// Template metadata
export const templateMeta = {
  id: 'hospitality-promo',
  name: 'Hospitality Promo',
  description: 'Warm, inviting design for cafes, restaurants & pubs promotions',
  category: 'promotion',
  defaultPurpose: 'promotion',
};

// Default form values for this template
export const getDefaultValues = (purpose = 'promotion') => {
  return {
    // Content
    headline: 'A Special Treat Just for You',
    subheadline: 'Because you deserve something delicious',
    body: "We've missed you! As one of our favorite guests, we wanted to share an exclusive offer. Come back and enjoy something special on us.",
    ctaText: 'Claim Your Offer',
    ctaUrl: '',
    // Offer Details
    offerBadge: '20% OFF',
    offerDescription: 'Your next visit',
    promoCode: 'WELCOME20',
    showPromoCode: true,
    expiryText: 'Offer expires December 31, 2024',
    showExpiry: true,
    // Brand
    companyName: 'The Cozy Corner Café',
    logoUrl: '',
    tagline: 'Where every cup tells a story',
    // Colors
    primaryColor: '#D97706', // Warm amber
    secondaryColor: '#F59E0B', // Lighter amber
    accentColor: '#92400E', // Dark brown
    backgroundColor: '#FFFBEB', // Warm cream
    // Social
    twitterUrl: '',
    facebookUrl: 'https://facebook.com',
    instagramUrl: 'https://instagram.com',
    tiktokUrl: '',
    // Footer
    footerText: 'Crafted with love for our valued guests',
    address: '123 Main Street, Your City',
  };
};

// Form fields configuration for Content tab
export const ContentFields = ({ formData, updateField }) => (
  <Flex direction="column" gap="4">
    
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Headline</Text>
      <TextField.Root 
        placeholder="A Special Treat Just for You" 
        value={formData.headline}
        onChange={(e) => updateField('headline', e.target.value)}
        style={{ alignSelf: 'stretch' }}
      />
    </Flex>
    
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Subheadline</Text>
      <TextField.Root 
        placeholder="Because you deserve something delicious" 
        value={formData.subheadline}
        onChange={(e) => updateField('subheadline', e.target.value)}
        style={{ alignSelf: 'stretch' }}
      />
    </Flex>
    
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Body text</Text>
      <TextArea 
        placeholder="We've missed you! As one of our favorite guests..." 
        value={formData.body}
        onChange={(e) => updateField('body', e.target.value)}
        rows={4}
        style={{ width: '100%' }}
      />
    </Flex>

    <Flex px="4" gap="2" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Offer details</Text>
      <Flex direction="column" gap="3" style={{ alignSelf: "stretch", padding: '12px', background: 'var(--amber-a3)', borderRadius: '8px' }}>
        <Flex gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
          <Text size="1" color="gray">Offer badge (e.g., "20% OFF", "FREE DESSERT")</Text>
          <TextField.Root 
            placeholder="20% OFF" 
            value={formData.offerBadge}
            onChange={(e) => updateField('offerBadge', e.target.value)}
            style={{ alignSelf: 'stretch' }}
          />
        </Flex>
        <Flex gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
          <Text size="1" color="gray">Offer description</Text>
          <TextField.Root 
            placeholder="Your next visit" 
            value={formData.offerDescription}
            onChange={(e) => updateField('offerDescription', e.target.value)}
            style={{ alignSelf: 'stretch' }}
          />
        </Flex>
        <Flex align="center" justify="between" style={{ alignSelf: "stretch" }}>
          <Flex gap="1" direction="column" style={{ alignItems: "flex-start", flex: 1 }}>
            <Text size="1" color="gray">Promo code</Text>
            <TextField.Root 
              placeholder="WELCOME20" 
              value={formData.promoCode}
              onChange={(e) => updateField('promoCode', e.target.value)}
              disabled={!formData.showPromoCode}
              style={{ alignSelf: 'stretch' }}
            />
          </Flex>
          <Flex align="center" gap="2" ml="3" mt="4">
            <Switch 
              checked={formData.showPromoCode} 
              onCheckedChange={(checked) => updateField('showPromoCode', checked)}
            />
            <Text size="1">Show</Text>
          </Flex>
        </Flex>
        <Flex align="center" justify="between" style={{ alignSelf: "stretch" }}>
          <Flex gap="1" direction="column" style={{ alignItems: "flex-start", flex: 1 }}>
            <Text size="1" color="gray">Expiry text</Text>
            <TextField.Root 
              placeholder="Offer expires December 31, 2024" 
              value={formData.expiryText}
              onChange={(e) => updateField('expiryText', e.target.value)}
              disabled={!formData.showExpiry}
              style={{ alignSelf: 'stretch' }}
            />
          </Flex>
          <Flex align="center" gap="2" ml="3" mt="4">
            <Switch 
              checked={formData.showExpiry} 
              onCheckedChange={(checked) => updateField('showExpiry', checked)}
            />
            <Text size="1">Show</Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
    
    <Flex px="4" pb="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Button text</Text>
      <TextField.Root 
        placeholder="Claim Your Offer" 
        value={formData.ctaText}
        onChange={(e) => updateField('ctaText', e.target.value)}
        style={{ alignSelf: 'stretch' }}
      />
    </Flex>
  </Flex>
);

// Form fields configuration for Brand tab
export const BrandFields = ({ 
  formData, 
  updateField,
  logoPreview,
  logoFileName,
  logoInputRef,
  handleLogoUpload,
  handleLogoClear,
  triggerLogoInput,
  backgroundPreview,
  backgroundFileName,
  backgroundInputRef,
  handleBackgroundImageUpload,
  handleBackgroundImageClear,
  triggerBackgroundInput,
}) => (
  <Flex direction="column" gap="4" style={{ alignSelf: "stretch" }}>

    <Flex p="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="1" weight="regular" color="gray">
        Customize your brand details to make the email truly yours.
      </Text>
    </Flex>
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px', marginTop: '-16px' }}/>
    
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Business name</Text>
      <TextField.Root 
        placeholder="The Cozy Corner Café" 
        value={formData.companyName}
        onChange={(e) => updateField('companyName', e.target.value)}
        style={{ width: '100%' }}
      />
    </Flex>

    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Tagline</Text>
      <TextField.Root 
        placeholder="Where every cup tells a story" 
        value={formData.tagline}
        onChange={(e) => updateField('tagline', e.target.value)}
        style={{ width: '100%' }}
      />
    </Flex>
    
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }}/>
    
    {/* Logo Upload */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Logo</Text>
      <Flex gap="2" style={{ width: '100%' }}>
        <input 
          type="file" 
          style={{ display: "none" }} 
          ref={logoInputRef} 
          onChange={handleLogoUpload} 
          accept="image/*" 
        />
        <Button
          type="button"
          size="2"
          variant="surface"
          color="gray"
          style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
          onClick={triggerLogoInput}
        >
          {logoPreview ? (
            <Tooltip content="Upload new to replace">
              <Text>{logoFileName?.length >= 18 ? `${logoFileName.slice(0, 18)}...` : logoFileName}</Text>
            </Tooltip>
          ) : (
            <>
              <UploadIcon />
              <Text>Upload</Text>
            </>
          )}
        </Button>
        {logoPreview && (
          <Button type="button" size="2" variant="surface" color="gray" onClick={handleLogoClear}>
            Clear
          </Button>
        )}
      </Flex>
      <Text size="1" color="gray" mt="1">Recommended dimensions: 200 x 96 px. Maximum file size: 1 MB.</Text>
    </Flex>
    
    {/* Logo URL (alternative) */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Or enter Logo URL</Text>
      <TextField.Root 
        placeholder="https://example.com/logo.png" 
        value={formData.logoUrl?.startsWith('data:') ? '' : (formData.logoUrl || '')}
        onChange={(e) => updateField('logoUrl', e.target.value)}
        style={{ width: '100%' }}
        disabled={!!logoPreview}
      />
      <Text size="1" color="gray" mt="1">Use a direct URL if you have the logo hosted elsewhere</Text>
    </Flex>
    
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }}/>
    
    {/* Background Image Upload */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Hero Image</Text>
      <Flex gap="2" style={{ width: '100%' }}>
        <input 
          type="file" 
          style={{ display: "none" }} 
          ref={backgroundInputRef} 
          onChange={handleBackgroundImageUpload} 
          accept="image/*" 
        />
        <Button
          type="button"
          size="2"
          variant="surface"
          color="gray"
          style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
          onClick={triggerBackgroundInput}
        >
          {backgroundPreview ? (
            <Tooltip content="Upload new to replace">
              <Text>{backgroundFileName?.length >= 18 ? `${backgroundFileName.slice(0, 18)}...` : backgroundFileName}</Text>
            </Tooltip>
          ) : (
            <>
              <UploadIcon />
              <Text>Upload</Text>
            </>
          )}
        </Button>
        {backgroundPreview && (
          <Button type="button" size="2" variant="surface" color="gray" onClick={handleBackgroundImageClear}>
            Clear
          </Button>
        )}
      </Flex>
      <Text size="1" color="gray" mt="1">Recommended dimensions: 600 x 400 px. Maximum file size: 1 MB.</Text>
    </Flex>

    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Address</Text>
      <TextField.Root 
        placeholder="123 Main Street, Your City" 
        value={formData.address}
        onChange={(e) => updateField('address', e.target.value)}
        style={{ width: '100%' }}
      />
    </Flex>
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }}/>
  </Flex>
);

// Form fields configuration for Colors tab
export const ColorsFields = ({ formData, updateField }) => (
  <Flex direction="column" gap="4">

    <Flex p="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="1" weight="regular" color="gray">
        Choose warm, inviting colors that reflect your hospitality brand.
      </Text>
    </Flex>
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px', marginTop: '-16px' }}/>

    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Primary color</Text>
      <Flex gap="2" align="center" style={{ width: '100%' }}>
        <input 
          type="color" 
          value={formData.primaryColor}
          onChange={(e) => updateField('primaryColor', e.target.value)}
          style={{ 
            width: 40, 
            height: 32, 
            borderRadius: 4,
          }}
        />
        <TextField.Root 
          value={formData.primaryColor}
          onChange={(e) => updateField('primaryColor', e.target.value)}
          style={{ width: '100%' }}
        />
      </Flex>
    </Flex>
    
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Secondary color</Text>
      <Flex gap="2" align="center" style={{ alignSelf: 'stretch' }}>
        <input 
          type="color" 
          value={formData.secondaryColor}
          onChange={(e) => updateField('secondaryColor', e.target.value)}
          style={{ 
            width: 40, 
            height: 32, 
            borderRadius: 4,
          }}
        />
        <TextField.Root 
          value={formData.secondaryColor}
          onChange={(e) => updateField('secondaryColor', e.target.value)}
          style={{ width: '100%' }}
        />
      </Flex>
    </Flex>

    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Accent color (text & details)</Text>
      <Flex gap="2" align="center" style={{ alignSelf: 'stretch' }}>
        <input 
          type="color" 
          value={formData.accentColor}
          onChange={(e) => updateField('accentColor', e.target.value)}
          style={{ 
            width: 40, 
            height: 32, 
            borderRadius: 4,
          }}
        />
        <TextField.Root 
          value={formData.accentColor}
          onChange={(e) => updateField('accentColor', e.target.value)}
          style={{ width: '100%' }}
        />
      </Flex>
    </Flex>

    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Background color</Text>
      <Flex gap="2" align="center" style={{ alignSelf: 'stretch' }}>
        <input 
          type="color" 
          value={formData.backgroundColor}
          onChange={(e) => updateField('backgroundColor', e.target.value)}
          style={{ 
            width: 40, 
            height: 32, 
            borderRadius: 4,
          }}
        />
        <TextField.Root 
          value={formData.backgroundColor}
          onChange={(e) => updateField('backgroundColor', e.target.value)}
          style={{ width: '100%' }}
        />
      </Flex>
    </Flex>
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }}/>
  </Flex>
);

// Form fields configuration for Social tab
export const SocialFields = ({ formData, updateField }) => (
  <Flex direction="column" gap="4">
    <Flex p="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="1" weight="regular" color="gray">
        Add your social media profile URLs to include social icons in the email footer.
      </Text>
    </Flex>
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px', marginTop: '-16px' }}/>
    
    {/* Instagram URL input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Flex gap="1" align="center">
        <InstagramLogoIcon />
        <Text size="2" weight="medium">Instagram URL</Text>
      </Flex>
      <TextField.Root 
        placeholder="https://instagram.com/yourcafe" 
        value={formData.instagramUrl}
        onChange={(e) => updateField('instagramUrl', e.target.value)}
        style={{ alignSelf: "stretch" }}
      />
    </Flex>
    
    {/* Facebook URL input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Flex gap="1" align="center">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 750 850" width="14" height="14" fill="var(--gray-12)" style={{ opacity: 1 }}>
          <path d="M659 8q34 0 59 24t24 58v577q0 35-24 59t-59 24H494V461h83l23-102H494v-69q0-22 13-41t44-19h49V123h-74q-79 0-117 42t-38 110v84h-83v102h83v289H82q-34 0-58-24T0 667V90q0-34 24-58T82 8z"/>
        </svg>
        <Text size="2" weight="medium">Facebook URL</Text>
      </Flex>
      <TextField.Root 
        placeholder="https://facebook.com/yourcafe" 
        value={formData.facebookUrl}
        onChange={(e) => updateField('facebookUrl', e.target.value)}
        style={{ alignSelf: "stretch" }}
      />
    </Flex>

    {/* Twitter/X URL input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Flex gap="1" align="center">
        <svg viewBox="0 0 1200 1227" width="12" height="12" fill="var(--gray-12)" xmlns="http://www.w3.org/2000/svg">
          <path d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z" fill="var(--gray-12)"/>
        </svg>
        <Text size="2" weight="medium">X URL</Text>
      </Flex>
      <TextField.Root 
        placeholder="https://twitter.com/yourcafe" 
        value={formData.twitterUrl}
        onChange={(e) => updateField('twitterUrl', e.target.value)}
        style={{ alignSelf: "stretch" }}
      />
    </Flex>
    
    {/* TikTok URL input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Flex gap="1" align="center">
        <svg viewBox="0 0 2859 3333" width="14" height="14" fill="var(--gray-12)" xmlns="http://www.w3.org/2000/svg">
          <path d="M2081 0c55 473 319 755 778 785v532c-266 26-499-61-770-225v995c0 1264-1378 1659-1932 753-356-583-138-1606 1004-1647v561c-87 14-180 36-265 65-254 86-398 247-358 531 77 544 1075 705 992-358V1h551z"/>
        </svg>
        <Text size="2" weight="medium">TikTok URL</Text>
      </Flex>
      <TextField.Root 
        placeholder="https://tiktok.com/@yourcafe" 
        value={formData.tiktokUrl}
        onChange={(e) => updateField('tiktokUrl', e.target.value)}
        style={{ alignSelf: "stretch" }}
      />
    </Flex>
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }}/>
    
    {/* Footer Text input field */}
    <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
      <Text size="2" weight="medium">Footer text</Text>
      <TextField.Root 
        placeholder="Crafted with love for our valued guests" 
        value={formData.footerText}
        onChange={(e) => updateField('footerText', e.target.value)}
        style={{ alignSelf: "stretch" }}
      />
    </Flex>
    <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }}/>
  </Flex>
);

// Generate MJML for this template
export const generateMjml = (formData) => {
  const { 
    headline, subheadline, body, ctaText, ctaUrl, 
    offerBadge, offerDescription, promoCode, showPromoCode,
    expiryText, showExpiry,
    primaryColor, secondaryColor, accentColor, backgroundColor,
    companyName, logoUrl, tagline, address, footerText,
    twitterUrl, facebookUrl, instagramUrl, tiktokUrl
  } = formData;

  const promoCodeSection = showPromoCode && promoCode ? `
      <mj-text align="center" padding="20px 0 5px">
        <div style="font-size: 12px; color: ${accentColor}; text-transform: uppercase; letter-spacing: 2px;">Use Code</div>
      </mj-text>
      <mj-text align="center" padding="0 0 20px">
        <div style="display: inline-block; background: ${backgroundColor}; border: 2px dashed ${primaryColor}; padding: 12px 30px; font-size: 24px; font-weight: bold; color: ${primaryColor}; letter-spacing: 4px; border-radius: 8px;">
          ${promoCode}
        </div>
      </mj-text>` : '';

  const expirySection = showExpiry && expiryText ? `
      <mj-text align="center" font-size="12px" color="#999999" padding="10px 0">
        ${expiryText}
      </mj-text>` : '';

  const logoSection = logoUrl ? `
      <mj-image src="${logoUrl}" width="80px" padding-bottom="10px" />` : `
      <mj-text align="center" font-size="32px" padding-bottom="5px">☕</mj-text>`;

  const socialIcons = [];
  if (instagramUrl) socialIcons.push(`<mj-social-element name="instagram" href="${instagramUrl}" background-color="${primaryColor}" />`);
  if (facebookUrl) socialIcons.push(`<mj-social-element name="facebook" href="${facebookUrl}" background-color="${primaryColor}" />`);
  if (twitterUrl) socialIcons.push(`<mj-social-element name="twitter" href="${twitterUrl}" background-color="${primaryColor}" />`);
  if (tiktokUrl) socialIcons.push(`<mj-social-element name="tiktok" href="${tiktokUrl}" background-color="${primaryColor}" />`);

  return `<mjml>
<mj-head>
  <mj-attributes>
    <mj-all font-family="'Helvetica Neue', Helvetica, Arial, sans-serif" />
    <mj-text font-size="16px" line-height="1.6" color="#333333" />
  </mj-attributes>
</mj-head>
<mj-body background-color="${backgroundColor}">
  <!-- Header with Logo -->
  <mj-section background-color="#ffffff" padding="30px 20px 20px">
    <mj-column>${logoSection}
      <mj-text align="center" font-size="22px" font-weight="bold" color="${accentColor}">
        ${companyName}
      </mj-text>
      ${tagline ? `<mj-text align="center" font-size="13px" color="#888888" font-style="italic" padding-top="5px">${tagline}</mj-text>` : ''}
    </mj-column>
  </mj-section>

  <!-- Offer Banner -->
  <mj-section background-color="${primaryColor}" padding="30px 20px">
    <mj-column>
      <mj-text align="center" font-size="48px" font-weight="bold" color="#ffffff" padding="0">
        ${offerBadge}
      </mj-text>
      <mj-text align="center" font-size="18px" color="rgba(255,255,255,0.9)" padding-top="5px">
        ${offerDescription}
      </mj-text>
    </mj-column>
  </mj-section>

  <!-- Main Content -->
  <mj-section background-color="#ffffff" padding="40px 30px">
    <mj-column>
      <mj-text align="center" font-size="26px" font-weight="600" color="${accentColor}" padding-bottom="10px">
        ${headline}
      </mj-text>
      <mj-text align="center" font-size="16px" color="#888888" font-style="italic" padding-bottom="20px">
        ${subheadline}
      </mj-text>
      <mj-text align="center" font-size="16px" line-height="1.8" color="#555555">
        ${body.split('\n').join('<br/>')}
      </mj-text>
      ${promoCodeSection}
      <mj-button background-color="${primaryColor}" color="#ffffff" href="${ctaUrl || '#'}" border-radius="30px" font-size="16px" padding="25px 0" font-weight="600" width="80%">
        ${ctaText}
      </mj-button>
      ${expirySection}
    </mj-column>
  </mj-section>

  <!-- Footer -->
  <mj-section background-color="${accentColor}" padding="30px 20px">
    <mj-column>
      ${socialIcons.length > 0 ? `<mj-social mode="horizontal" align="center" icon-size="25px" icon-padding="10px">${socialIcons.join('')}</mj-social>` : ''}
      <mj-text align="center" font-size="14px" color="rgba(255,255,255,0.9)" padding-top="15px">
        ${companyName}
      </mj-text>
      ${address ? `<mj-text align="center" font-size="12px" color="rgba(255,255,255,0.7)" padding-top="5px">${address}</mj-text>` : ''}
      <mj-text align="center" font-size="11px" color="rgba(255,255,255,0.6)" padding-top="15px">
        ${footerText}
      </mj-text>
      <mj-text align="center" font-size="11px" color="rgba(255,255,255,0.5)" padding-top="10px">
        © ${new Date().getFullYear()} ${companyName}. All rights reserved.
      </mj-text>
    </mj-column>
  </mj-section>
</mj-body>
</mjml>`;
};

// Generate Preview HTML for this template
export const generatePreviewHtml = (formData) => {
  const { 
    headline, subheadline, body, ctaText, ctaUrl, 
    offerBadge, offerDescription, promoCode, showPromoCode,
    expiryText, showExpiry,
    primaryColor, secondaryColor, accentColor, backgroundColor,
    companyName, logoUrl, tagline, address, footerText,
    twitterUrl, facebookUrl, instagramUrl
  } = formData;

  const logoHtml = logoUrl 
    ? `<img src="${logoUrl}" alt="Logo" style="width: 80px; margin-bottom: 10px;" />`
    : `<div style="font-size: 40px; margin-bottom: 10px;">☕</div>`;

  const promoCodeSection = showPromoCode && promoCode ? `
    <div style="margin: 25px 0 10px;">
      <div style="font-size: 12px; color: ${accentColor}; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 8px;">Use Code</div>
      <div style="display: inline-block; background: ${backgroundColor}; border: 2px dashed ${primaryColor}; padding: 12px 30px; font-size: 24px; font-weight: bold; color: ${primaryColor}; letter-spacing: 4px; border-radius: 8px;">
        ${promoCode}
      </div>
    </div>` : '';

  const expirySection = showExpiry && expiryText ? `
    <p style="font-size: 12px; color: #999999; margin-top: 15px;">${expiryText}</p>` : '';

  return `
    <div style="background-color: ${backgroundColor}; padding: 20px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">
      <div style="max-width: 500px; margin: 0 auto; box-shadow: 0 8px 30px rgba(0,0,0,0.12); border-radius: 16px; overflow: hidden;">
        
        <!-- Header -->
        <div style="background-color: #ffffff; padding: 30px 20px 20px; text-align: center;">
          ${logoHtml}
          <h2 style="font-size: 22px; font-weight: bold; color: ${accentColor}; margin: 0;">
            ${companyName}
          </h2>
          ${tagline ? `<p style="font-size: 13px; color: #888888; font-style: italic; margin: 8px 0 0;">${tagline}</p>` : ''}
        </div>
        
        <!-- Offer Banner -->
        <div style="background: linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%); padding: 35px 20px; text-align: center;">
          <div style="font-size: 52px; font-weight: bold; color: #ffffff; line-height: 1;">
            ${offerBadge}
          </div>
          <p style="font-size: 18px; color: rgba(255,255,255,0.9); margin: 8px 0 0;">
            ${offerDescription}
          </p>
        </div>
        
        <!-- Main Content -->
        <div style="background-color: #ffffff; padding: 40px 30px; text-align: center;">
          <h1 style="font-size: 26px; font-weight: 600; color: ${accentColor}; margin: 0 0 10px;">
            ${headline}
          </h1>
          <p style="font-size: 16px; color: #888888; font-style: italic; margin: 0 0 20px;">
            ${subheadline}
          </p>
          <p style="font-size: 16px; line-height: 1.8; color: #555555; margin: 0;">
            ${body.split('\n').join('<br/>')}
          </p>
          
          ${promoCodeSection}
          
          <a href="${ctaUrl || '#'}" style="display: inline-block; background: linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%); color: white; padding: 16px 50px; text-decoration: none; border-radius: 30px; font-weight: 600; font-size: 16px; margin-top: 25px; box-shadow: 0 4px 15px ${primaryColor}40;">
            ${ctaText}
          </a>
          
          ${expirySection}
        </div>
        
        <!-- Footer -->
        <div style="background-color: ${accentColor}; padding: 30px 20px; text-align: center;">
          <div style="margin-bottom: 15px;">
            ${instagramUrl ? `<a href="${instagramUrl}" style="color: #ffffff; text-decoration: none; margin: 0 10px; font-size: 18px;">📸</a>` : ''}
            ${facebookUrl ? `<a href="${facebookUrl}" style="color: #ffffff; text-decoration: none; margin: 0 10px; font-size: 18px;">👍</a>` : ''}
            ${twitterUrl ? `<a href="${twitterUrl}" style="color: #ffffff; text-decoration: none; margin: 0 10px; font-size: 18px;">🐦</a>` : ''}
          </div>
          <p style="font-size: 14px; color: rgba(255,255,255,0.9); margin: 0;">
            ${companyName}
          </p>
          ${address ? `<p style="font-size: 12px; color: rgba(255,255,255,0.7); margin: 8px 0 0;">${address}</p>` : ''}
          <p style="font-size: 11px; color: rgba(255,255,255,0.6); margin: 15px 0 0;">
            ${footerText}
          </p>
          <p style="font-size: 11px; color: rgba(255,255,255,0.5); margin: 10px 0 0;">
            © ${new Date().getFullYear()} ${companyName}. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  `;
};

// Default export with all template utilities
const HospitalityPromoTemplate = {
  meta: templateMeta,
  getDefaultValues,
  ContentFields,
  BrandFields,
  ColorsFields,
  SocialFields,
  generateMjml,
  generatePreviewHtml,
};

export default HospitalityPromoTemplate;
